import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { backgroundUrl, NARROW_WIDTH } from '../responsiveImage';

const SLIDE_INTERVAL_MS = 5000;
const FADE_MS = 1000;

function HeroSection({ hero, isAuthenticated, onEdit }) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [loaded, setLoaded] = useState(() => new Set());

  // These are CSS backgrounds, so srcset cannot help — the width has to be
  // chosen here. Re-evaluated on resize/rotate, which matters on a tablet.
  const [viewportWidth, setViewportWidth] = useState(
    () => (typeof window === 'undefined' ? NARROW_WIDTH * 2 : window.innerWidth)
  );

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);

  const stored = useMemo(() => hero?.backgroundImages || [], [hero]);
  const images = useMemo(
    () => stored.map(url => backgroundUrl(url, viewportWidth)),
    [stored, viewportWidth]
  );
  const hasImages = images.length > 0;
  const imagesKey = images.join('|');

  // Decode every background up front. Previously the next image only started
  // downloading at the moment it was swapped in, so on a slow connection the
  // hero painted its dark overlay over an empty background — a grey flash that
  // lasted exactly as long as the download.
  useEffect(() => {
    if (!hasImages) return undefined;

    let cancelled = false;

    // Sequentially, not all at once. Firing every request together would put
    // ~1.5MB in flight on first paint and starve the first image — the worst
    // outcome on a phone. One at a time gives image 1 the whole connection,
    // and at five seconds a slide the rest are ready long before their turn.
    const preload = (src) => new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        if (!cancelled) {
          setLoaded((previous) => {
            if (previous.has(src)) return previous;
            const next = new Set(previous);
            next.add(src);
            return next;
          });
        }
        resolve();
      };
      // A broken URL simply never joins `loaded`, so the slideshow skips it.
      img.onerror = () => resolve();
      img.src = src;
    });

    (async () => {
      for (const src of images) {
        if (cancelled) return;
        await preload(src);
      }
    })();

    return () => { cancelled = true; };
    // imagesKey covers the contents; `images` identity alone would re-run needlessly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imagesKey, hasImages]);

  // Never advance onto an image that has not finished decoding — that is the
  // only way a blank frame can reach the screen.
  const findLoadedIndex = useCallback((from, direction) => {
    const count = images.length;
    for (let step = 1; step <= count; step++) {
      const index = ((from + direction * step) % count + count) % count;
      if (loaded.has(images[index])) return index;
    }
    return from; // nothing decoded yet: hold on the current frame
  }, [images, loaded]);

  const goToPrevious = useCallback(() => {
    setCurrentImageIndex((previous) => findLoadedIndex(previous, -1));
  }, [findLoadedIndex]);

  const goToNext = useCallback(() => {
    setCurrentImageIndex((previous) => findLoadedIndex(previous, 1));
  }, [findLoadedIndex]);

  useEffect(() => {
    if (images.length <= 1) return undefined;

    // Respect the OS "reduce motion" setting: no automatic slideshow.
    const reduceMotion = window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) return undefined;

    const interval = setInterval(goToNext, SLIDE_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [images.length, goToNext]);

  return (
    <section
      id="home"
      className="hero-gradient flex items-center justify-center text-white text-center relative"
      style={{ height: '100vh', marginTop: '3rem', overflow: 'hidden' }}
    >
      {/* One layer per image, cross-faded with opacity. Unlike background-image,
          opacity is animatable, so this is an actual fade rather than a cut.
          A layer stays fully transparent until its image has decoded. */}
      {images.map((src, index) => (
        <div
          key={src}
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `url(${src})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: index === currentImageIndex && loaded.has(src) ? 1 : 0,
            transition: `opacity ${FADE_MS}ms ease-in-out`,
            zIndex: 0,
          }}
        />
      ))}

      {/* The darkening wash lives on its own layer above the photos. It used to
          be baked into the same background-image shorthand, which is what made
          it visible over an empty background. It fades in with the photo so it
          does not double-darken the gradient fallback. */}
      {hasImages && (
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.4)',
            opacity: loaded.has(images[currentImageIndex]) ? 1 : 0,
            transition: `opacity ${FADE_MS}ms ease-in-out`,
            zIndex: 1,
          }}
        />
      )}

      {isAuthenticated && (
        <button
          onClick={onEdit}
          aria-label="Edit hero section"
          className="absolute top-8 right-8 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-bold transition flex items-center gap-2"
          style={{ zIndex: 10 }}
        >
          ✏️ Edit Hero
        </button>
      )}

      {/* Slideshow Controls */}
      {hasImages && images.length > 1 && (
        <>
          <button
            onClick={goToPrevious}
            aria-label="Previous background image"
            className="absolute left-8 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-75 text-white w-12 h-12 rounded-full flex items-center justify-center transition"
            style={{ zIndex: 10 }}
          >
            <span aria-hidden="true">←</span>
          </button>
          <button
            onClick={goToNext}
            aria-label="Next background image"
            className="absolute right-8 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-75 text-white w-12 h-12 rounded-full flex items-center justify-center transition"
            style={{ zIndex: 10 }}
          >
            <span aria-hidden="true">→</span>
          </button>

          {/* Dots indicator */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2" style={{ zIndex: 10 }}>
            {images.map((src, index) => (
              <button
                key={src}
                onClick={() => loaded.has(src) && setCurrentImageIndex(index)}
                aria-label={`Show background image ${index + 1}`}
                aria-current={index === currentImageIndex}
                className={`w-3 h-3 rounded-full transition ${
                  index === currentImageIndex ? 'bg-white' : 'bg-white bg-opacity-50'
                }`}
              />
            ))}
          </div>
        </>
      )}

      <div className="px-4 relative" style={{ zIndex: 2 }}>
        <h1 className="text-6xl md:text-7xl font-bold mb-6" style={{ textShadow: '0 4px 6px rgba(0,0,0,0.3)' }}>
          {hero?.title || 'Welcome to Vivat Alfa Winery'}
        </h1>
        <p className="text-2xl md:text-3xl mb-8">
          {hero?.subtitle || 'European Style Estate Bottled Wines - Since 2013'}
        </p>
        <a href="#wines" className="inline-block px-10 py-4 bg-wine hover:bg-wine-light text-white text-lg rounded-lg transition-all" style={{ backgroundColor: '#722f37', border: '2px solid #f4bc57' }}>
          {hero?.buttonText || 'Explore Our Collection'}
        </a>
      </div>
    </section>
  );
}

export default HeroSection;
